import { api, unwrap, BASE_URL, getStoredToken } from './api';

export type AiActionKey =
  | 'improve'
  | 'shorten'
  | 'expand'
  | 'tone_formal'
  | 'tone_casual'
  | 'fix_grammar'
  | 'translate'
  | 'seo_title'
  | 'seo_description'
  | 'excerpt'
  | 'title_suggest'
  | 'alt_text'
  | 'continue'
  | 'rewrite_html'
  | 'block_suggest';

export type AiFormat = 'plain' | 'html' | 'json';

export interface AiActionMeta {
  key: AiActionKey;
  label: string;
  format: AiFormat;
}

export interface AiActionCatalogue {
  provider: string;
  mock: boolean;
  actions: AiActionMeta[];
}

export interface AiGenerateResult {
  text: string;
  provider: string;
  model: string;
  usage: { input: number; output: number } | null;
}

export interface AiGenerateOptions {
  targetLocale?: string;
  tone?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Lazy cache for the action catalogue so the dropdown loads instantly after
 * the first call.
 */
let cataloguePromise: Promise<AiActionCatalogue> | null = null;

export function getAiCatalogue(): Promise<AiActionCatalogue> {
  if (!cataloguePromise) {
    cataloguePromise = unwrap<AiActionCatalogue>(api.get('/ai/actions'));
  }
  return cataloguePromise;
}

export async function aiGenerate(
  action: AiActionKey,
  input: string,
  options: AiGenerateOptions = {},
): Promise<AiGenerateResult> {
  return unwrap<AiGenerateResult>(
    api.post('/ai/generate', { action, input, options }),
  );
}

export async function aiDescribeImage(imageUrl: string): Promise<AiGenerateResult> {
  return unwrap<AiGenerateResult>(api.post('/ai/vision', { image: imageUrl }));
}

export interface AiStreamHandlers {
  onToken: (text: string, fullSoFar: string) => void;
  onDone?: (full: string) => void;
  onError?: (message: string) => void;
  signal?: AbortSignal;
}

/**
 * Stream tokens for an AI action over SSE. Yields each chunk to `onToken`
 * with the accumulated text so the caller can render progressive output.
 *
 * Returns a promise that resolves with the full text when the stream ends,
 * or rejects on transport/server error.
 */
export async function aiGenerateStream(
  action: AiActionKey,
  input: string,
  options: AiGenerateOptions = {},
  handlers: AiStreamHandlers,
): Promise<string> {
  const token = getStoredToken();
  const res = await fetch(`${BASE_URL}/ai/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ action, input, options }),
    signal: handlers.signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    const msg = text || `HTTP ${res.status}`;
    handlers.onError?.(msg);
    throw new Error(msg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        for (const line of raw.split('\n')) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const evt = JSON.parse(payload) as
              | { type: 'token'; text: string }
              | { type: 'done' }
              | { type: 'error'; message: string };
            if (evt.type === 'token') {
              full += evt.text;
              handlers.onToken(evt.text, full);
            } else if (evt.type === 'done') {
              handlers.onDone?.(full);
              return full;
            } else if (evt.type === 'error') {
              handlers.onError?.(evt.message);
              throw new Error(evt.message);
            }
          } catch (e) {
            if (e instanceof Error) handlers.onError?.(e.message);
            throw e;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  handlers.onDone?.(full);
  return full;
}

/**
 * Curated subsets of actions for different field contexts. Keeps the dropdown
 * focused on what's relevant for the input the user is editing.
 */
export const AI_PRESETS: Record<string, AiActionKey[]> = {
  title: ['improve', 'shorten', 'title_suggest', 'translate'],
  excerpt: ['improve', 'shorten', 'expand', 'excerpt', 'translate'],
  seoTitle: ['seo_title', 'shorten'],
  seoDescription: ['seo_description', 'improve'],
  plainText: ['improve', 'shorten', 'expand', 'tone_formal', 'tone_casual', 'fix_grammar', 'translate'],
  richText: ['rewrite_html', 'improve', 'shorten', 'expand', 'continue', 'tone_formal', 'tone_casual', 'fix_grammar', 'translate'],
  alt: ['alt_text'],
};
